// React Imports
import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";

// External Libraries
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

// Styles
import S from "./SignupPage.module.scss";

// Helpers

import { getLoginClient } from "sj-ui-toolkit/src/client";

// Constants
import { SIGNUP_EMAIL_KEY, SJ_FE_MONOREPO } from "../../../constants/signup";
import { LoginAppRoutes } from "sj-ui-toolkit/src/constants/routes";

interface SignupFormData {
  email: string;
  password: string;
}

export default function SignupPage() {
  const [formData, setFormData] = useState<SignupFormData>({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get("email");
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (navigateTimeoutRef.current !== null) {
        clearTimeout(navigateTimeoutRef.current);
      }
    };
  }, []);
  const loginClient = getLoginClient();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }) as SignupFormData);
  };

  useEffect(() => {
    if (!emailFromQuery) return;
    setFormData(prev => ({ ...prev, email: emailFromQuery }));
  }, [emailFromQuery]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const registerResponse = await loginClient.register({
        email: formData.email.toLowerCase(),
        password: formData.password,
        metadata: { signUpFrom: SJ_FE_MONOREPO },
      });

      if (registerResponse.error) {
        throw new Error(registerResponse.error.message || "Failed to sign up");
      }

      localStorage.setItem(SIGNUP_EMAIL_KEY, formData.email);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Your account has been created successfully.",
      });

      navigateTimeoutRef.current = setTimeout(() => {
        navigateTimeoutRef.current = null;
        navigate(LoginAppRoutes.routes.verify.route);
        setLoading(false);
      }, 2000);
    } catch (errorMessage) {
      setLoading(false);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: String(errorMessage),
      });
    }
  };

  return (
    <LoginLayout title={"Create Your Account"}>
      <Toast ref={toast} />
      <form onSubmit={handleSubmit} className={`p-fluid ${S.root}`} data-testid="signup-form">
        <div className="field">
          <InputText
            id="email"
            name="email"
            data-testid="signup-email-input"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            required
            aria-required="true"
            aria-label="Email address"
          />
        </div>
        <div className="field">
          <Password
            id="password"
            name="password"
            data-testid="signup-password-input"
            value={formData.password}
            onChange={handleChange}
            placeholder="Password"
            toggleMask
            feedback={false}
            required
            aria-required="true"
            aria-label="Password"
            pt={{ hideIcon: { className: S.pwIcon }, showIcon: { className: S.pwIcon } }}
          />
        </div>
        <Button
          type="submit"
          data-testid="signup-submit-button"
          label="Sign Up"
          loading={loading}
          disabled={loading}
        />
      </form>
    </LoginLayout>
  );
}
